import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { IValidation } from '../../../interfaces/IError';

import CheckmateSelectHelper from '../../../utilities/CheckmateSelectHelper';
import Authorization from '../../../stores/Authorization';

import { UserRightsEnum, CaseFileStaticItems, DataScopesEnum } from '../../../utilities/Constants';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useZoneContext } from '../../../contexts/useZoneContext';

import { useGetOrganizationByCaseModel } from '../../../shared/react-query-hooks/useGetOrganizationByCaseModel';
import { useQuadCaseStaticItemsByAuthorizedZones } from '../../../shared/react-query-hooks/useQuadCaseStaticItemsByAuthorizedZones';
import { useCaseStatusTypesLookup } from '../../../shared/react-query-hooks/useCaseStatusTypesLookup';
import { useDefenseCounselsLookup } from '../../../shared/react-query-hooks/useDefenseCounselsLookup';
import { useGetAcknowledgmentStatusTypes } from '../../../shared/react-query-hooks/useGetAcknowledgmentStatusTypes';
import { useGetApprovalStatusTypes } from '../../../shared/react-query-hooks/useGetApprovalStatusTypes';
import { useGetDepositionCategories } from '../../../shared/react-query-hooks/useGetDepositionCategories';
import { useGetDepositionStatusTypes } from '../../../shared/react-query-hooks/useGetDepositionStatusTypes';
import { useGetNoteReviewStatusTypes } from '../../../shared/react-query-hooks/useGetNoteReviewStatusTypes';
import { useGetNotePurposeTypes } from '../../../shared/react-query-hooks/useGetNotePurposeTypes';
import { useGetPackageDeals } from '../../../shared/react-query-hooks/useGetPackageDeals';
import { useGetUsStatesLookup } from '../../../shared/react-query-hooks/useUsStatesLookup';
import { useNoteCategoryTypes } from '../../../shared/react-query-hooks/useNoteCategoryTypes';

import { Loader } from '../../../components/shared/Loader';
import { Query } from '../../query/Query';
import { useGetQuadAssertionDescriptorsByAuthorizedZones } from '../../../shared/react-query-hooks/useQuadAssertionDescriptorsByAuthorizedZones';
import { useGetQuadUsersByZone } from '../../../components/quad/same-case-easy-update/useGetQuadUsersByZone';
import { useQuadCaseManagersInZone } from '../../../shared/react-query-hooks/useQuadCaseManagersInZone';

export function QuadQueryWrapper() {
    const { state: locationState } = useLocation();
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();

    const caseFileModelId = 1; // todo:aj Hardcoded or can be passed via props later

    const organizationsByCaseModelQuery = useGetOrganizationByCaseModel(caseFileModelId);
    const allowedOrganizations = Authorization.getUserOrganizationsWithRight(
        UserRightsEnum.ViewCaseList,
        user
    );

    const filteredOrganizationsForCaseModel = useMemo(() => {
        const modelOrgs = organizationsByCaseModelQuery.data ?? [];
        const allowed = allowedOrganizations ?? [];
        return modelOrgs.filter((org) =>
            allowed.some((allowedOrg) => allowedOrg.guid === org.guid)
        );
    }, [organizationsByCaseModelQuery.data, allowedOrganizations]);

    const selectedOrgGuids = useMemo(
        () => CheckmateSelectHelper.getOrgGuids(filteredOrganizationsForCaseModel),
        [filteredOrganizationsForCaseModel]
    );

    const hasValidQuadOrgs = selectedOrgGuids.length > 0;

    // Quad-specific lookups
    const assertionDescriptorsLookup = useGetQuadAssertionDescriptorsByAuthorizedZones(
        caseFileModelId,
        selectedOrgGuids,
        hasValidQuadOrgs
    );
    const staticItems = useQuadCaseStaticItemsByAuthorizedZones(
        caseFileModelId,
        selectedOrgGuids,
        hasValidQuadOrgs
    );

    // Shared lookups
    const caseManagersLookup = useQuadCaseManagersInZone(selectedOrgGuids[0], hasValidQuadOrgs);
    const caseStatusTypesLookup = useCaseStatusTypesLookup();
    const defenseCounselsLookup = useDefenseCounselsLookup();
    const acknowledgmentStatusTypes = useGetAcknowledgmentStatusTypes();
    const approvalStatusTypes = useGetApprovalStatusTypes();
    const depositionCategories = useGetDepositionCategories();
    const depositionStatusTypes = useGetDepositionStatusTypes();
    const noteReviewStatusTypes = useGetNoteReviewStatusTypes();
    const noteCategoryTypes = useNoteCategoryTypes();
    const notePurposes = useGetNotePurposeTypes();
    const usStatesLookup = useGetUsStatesLookup();

    const zoneUsersApi = useGetQuadUsersByZone(selectedOrgGuids[0], DataScopesEnum.National.Value, hasValidQuadOrgs);

    const packageDeals = useGetPackageDeals(
        !!(staticItems.data ?? []).find((si) => si.id === CaseFileStaticItems.PackageSettlement.Id)
    );

    const isLoadingLookups =
        organizationsByCaseModelQuery.isLoading ||
        assertionDescriptorsLookup.isLoading ||
        staticItems.isLoading ||
        caseManagersLookup.isLoading ||
        caseStatusTypesLookup.isLoading ||
        defenseCounselsLookup.isLoading ||
        acknowledgmentStatusTypes.isLoading ||
        approvalStatusTypes.isLoading ||
        depositionCategories.isLoading ||
        depositionStatusTypes.isLoading ||
        noteReviewStatusTypes.isLoading ||
        noteCategoryTypes.isLoading ||
        notePurposes.isLoading ||
        usStatesLookup.isLoading ||
        zoneUsersApi.isLoading ||
        packageDeals.isLoading;

    const hasLookupError =
        organizationsByCaseModelQuery.isError ||
        assertionDescriptorsLookup.isError ||
        staticItems.isError ||
        caseManagersLookup.isError ||
        caseStatusTypesLookup.isError ||
        defenseCounselsLookup.isError ||
        acknowledgmentStatusTypes.isError ||
        approvalStatusTypes.isError ||
        depositionCategories.isError ||
        depositionStatusTypes.isError ||
        noteReviewStatusTypes.isError ||
        noteCategoryTypes.isError ||
        notePurposes.isError ||
        usStatesLookup.isError ||
        zoneUsersApi.isError ||
        packageDeals.isError;

    let validation: IValidation = {};

    if (hasLookupError) {
        validation = {
            ...validation,
            lookups: [
                'An unhandled error occurred while retrieving Query Criteria data. Please refresh your page and try again. If the issue persists, contact your administrator',
            ],
        };
    }

    if (
        isLoadingLookups ||
        !filteredOrganizationsForCaseModel ||
        filteredOrganizationsForCaseModel.length === 0
    )
        return <Loader />;

    return (
        <Query
            acknowledgmentStatusTypes={acknowledgmentStatusTypes?.data ?? []}
            approvalStatusTypes={approvalStatusTypes?.data ?? []}
            assertionDescriptors={assertionDescriptorsLookup?.data ?? []}
            caseManagers={caseManagersLookup?.data ?? []}
            caseStatuses={caseStatusTypesLookup?.data ?? []}
            defenseCounsels={defenseCounselsLookup?.data ?? []}
            depositionCategories={depositionCategories?.data ?? []}
            depositionStatusTypes={depositionStatusTypes?.data ?? []}
            noteReviewStatusTypes={noteReviewStatusTypes?.data ?? []}
            noteCategoryTypes={noteCategoryTypes?.data ?? []}
            notePurposeTypes={notePurposes?.data ?? []}
            packageDeals={packageDeals?.data ?? []}
            redirectModel={locationState?.redirectModel}
            staticItems={staticItems?.data ?? []}
            user={user}
            usStates={usStatesLookup?.data ?? []}
            validation={validation}
            zoneUsers={zoneUsersApi?.data ?? []}
            zoneContext={zoneContext}
            isQuadQuery={true}
            quadOrganizations={filteredOrganizationsForCaseModel}
        />
    );
}
